/**
 * Вкладка «Доставка» панели «Чатов» (владелец 22.10.26, редизайн по концепту 26.10.26): доставка сделки к
 * началу проката (салон → клиент) и забор в конце (клиент → салон).
 *
 * Сверху — два направления карточками: у каждого день и статус видны сразу. У выбранного — переключатель
 * «нужна / не нужна» (`DeliveryNeed`, право `delivery_edit`) и путь шагами Данные → Тариф → Курьер →
 * Доставлено/В салоне; внизу — одно главное действие текущего шага. Одна форма на направление: адрес с
 * частями, окно и срок салона по времени салона, телефон, пожелание и общий комментарий курьеру — пишет
 * `POST /api/deals/{id}/delivery/{н}` (**боевой Bitrix и договор CRM**). Расхождение времени (в сделке «до
 * 08:00», в тексте адреса «с 10 до 12», начала окна нет) — предупреждением, сохранить без начала окна нельзя.
 * Смена адреса или окна сбрасывает расчёт и выбранный тариф. Заказ курьера (право `delivery_order`):
 * тариф → цена клиенту → «Заказать» — заявка в службе, строка «Доставка» в договоре, курьер в сделке.
 * Курьер не из prokat и статус вручную — поля Bitrix «курьер», «телефон курьера», «статус».
 * Подвкладки (`&dsub=courier|track`): «Курьер» и «Трекинг» (`DealTracks`).
 * Спеки `docs/specs/2026-10-22-delivery-tab-design.md`, `docs/specs/2026-10-22-delivery-order-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, History, Maximize2, MapPin, Package, RefreshCw, Truck, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { DealTracks } from "@/components/chat/DealTracks";
import { HourPick, WindowPick } from "@/components/chat/DeliveryWhen";
import type { OnOffer } from "@/components/chat/SerialPhotos";
import { DeliveryNeed } from "@/components/DeliveryNeed";
import { ApiFailure } from "@/lib/api";
import {
  type CourierOrder,
  type DealDelivery,
  type DealDeliverySide,
  type DeliveryForm,
  type Geo,
  type Offer,
  type Pick,
  type Suggest,
  type Tariff,
  cancelCourier,
  clientPrice,
  dealDeliveryKey,
  deliveryTariff,
  loadDealDelivery,
  mapUrl,
  mapsUrl,
  orderCourier,
  refreshCourier,
  saveDealDelivery,
  suggestAddress,
} from "@/lib/dealDelivery";
import { CONTRACT_TEXT, type DeliveryEdit, type Direction, contractFailed, dm, saveDelivery } from "@/lib/delivery";
import { dealTracksKey, loadDealTracks } from "@/lib/dealTracks";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]";
const note = "p-4 text-[12.5px] text-fg-muted";
const input =
  "h-8 w-full min-w-0 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg placeholder:text-fg-faint";
const field = "grid min-w-0 gap-1 text-[11.5px] text-fg-muted";
const btn =
  "inline-flex h-8 items-center justify-center gap-1 rounded-[var(--radius-ui)] border border-border px-3 text-[12.5px] text-fg hover:bg-bg-hover disabled:opacity-50";
const btnMain =
  "inline-flex h-9 items-center justify-center gap-1 rounded-[var(--radius-ui)] bg-accent px-3 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50";
const link = "text-[12px] text-accent hover:underline disabled:opacity-50";
const rub = (n: number) => `${Math.round(n).toLocaleString("ru-RU")} ₽`;
const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;
const today = () => new Date().toLocaleDateString("sv-SE");

const DIRS: { id: Direction; label: string; what: string; route: string }[] = [
  { id: "start", label: "К началу проката", what: "Доставка", route: "салон → клиент" },
  { id: "end", label: "Конец · забор", what: "Забор", route: "клиент → салон" },
];
const OFF: Record<Direction, string> = { start: "не нужна", end: "не нужен" };
const STEPS: Record<Direction, string[]> = {
  start: ["Данные", "Тариф", "Курьер", "Доставлено"],
  end: ["Данные", "Тариф", "Курьер", "В салоне"],
};
const TONE: Record<string, string> = {
  need: "text-err",
  go: "text-warn",
  done: "text-ok",
  cancel: "text-fg-faint",
  "": "text-fg-faint",
};

/** `2026-10-23T14:00` → «14:00», другой день — «23.10 14:00». */
const at = (t: string | null, day: string) => (!t ? "—" : t.slice(0, 10) === day ? t.slice(11, 16) : `${dm(t)} ${t.slice(11, 16)}`);

/** Шаг направления: не нужна → данные → тариф → курьер в пути → готово. */
type Stage = "none" | "edit" | "ready" | "courier" | "done";

const active = (s: DealDeliverySide) => s.status_kind !== "cancel" && !!(s.status || s.address);

function stageOf(s: DealDeliverySide, live: boolean): Stage {
  if (!active(s)) return "none";
  if (s.status_kind === "done") return "done";
  if (live || s.status_kind === "go") return "courier";
  return s.address && s.time && s.window ? "ready" : "edit";
}

const STEP: Record<Stage, number> = { none: -1, edit: 0, ready: 1, courier: 2, done: 3 };

/** Чего ждём от службы по статусу Bitrix: назначен → выехал → (забрал) → готово. */
function next(s: DealDeliverySide, direction: Direction, statuses: DealDelivery["statuses"][Direction]): string {
  if (s.status_kind === "need") return "ждём назначения курьера";
  const i = statuses.filter((x) => x.kind === "go").findIndex((x) => x.code === s.status);
  if (i === 0) return "ждём выезда";
  if (direction === "start") return "ждём вручения клиенту";
  return i === 2 ? "ждём возврата в салон" : "ждём забора у клиента";
}

/** «с 10 до 12» в тексте адреса — окно, которое раньше писали в адрес руками. */
const INTERVAL = /с\s*(\d{1,2})(?:[:.]\d{2})?\s*до\s*(\d{1,2})(?:[:.]\d{2})?/i;

function addressWindow(a: string): [number, number] | null {
  const m = INTERVAL.exec(a);
  if (!m) return null;
  const f = Number(m[1]);
  const t = Number(m[2]);
  return f < t && t <= 23 ? [f, t] : null;
}

/** Форма из карточки: окно — из своей строки, нет — начало пустое, конец — срок Bitrix «не позднее». */
function initial(s: DealDeliverySide, phones: string[]): DeliveryForm {
  const from = s.window?.from ? Number(s.window.from.slice(0, 2)) : -1;
  const to = s.window?.to ? Number(s.window.to.slice(0, 2)) : s.time ? Number(s.time.slice(11, 13)) : 14;
  return {
    address: s.parts.street || s.address,
    uri: null,
    lat: s.geo?.lat ?? null,
    lon: s.geo?.lon ?? null,
    apartment: s.parts.apartment,
    entrance: s.parts.entrance,
    floor: s.parts.floor,
    intercom: s.parts.intercom,
    day: s.time?.slice(0, 10) ?? s.day ?? today(),
    from,
    to,
    phone: s.phone || phones[0] || "",
    note: s.note,
    salon_at: s.time_salon ?? "",
    comment: s.comment,
  };
}

/** Срок салона одним кликом от окна: доставка — забрать из салона за 1–2 ч до начала (нет начала —
 * до конца), забор — вернуть через 1–2 ч после конца окна. */
function salonSuggest(f: DeliveryForm, direction: Direction): { label: string; value: string }[] {
  if (!f.day) return [];
  const base = direction === "start" ? (f.from >= 0 ? f.from : f.to) : f.to;
  const steps = direction === "start" ? [-1, -2] : [1, 2];
  return steps
    .map((n) => ({ n, h: base + n }))
    .filter(({ h }) => h >= 0 && h <= 23)
    .map(({ n, h }) => ({ label: direction === "start" ? `за ${-n} ч до окна` : `через ${n} ч после окна`, value: `${f.day}T${hh(h)}` }));
}

/** Почему форму нельзя сохранить; пусто — можно. Порядок сроков сервер проверяет так же. */
type Problem = { where: "" | "address" | "window" | "salon"; text: string };

function problem(f: DeliveryForm, direction: Direction): Problem {
  const verb = direction === "start" ? "доставки" : "забора";
  if (f.address.trim().length < 5) return { where: "address", text: "Укажите адрес: город, улицу и дом" };
  if (!f.day || f.from < 0)
    return { where: "window", text: `Выберите окно ${verb} — с какого по какой час курьер приедет к клиенту` };
  if (f.from >= f.to) return { where: "window", text: "Конец окна должен быть позже начала" };
  if (f.salon_at.length === 16) {
    const end = `${f.day}T${hh(f.to)}`;
    if (direction === "start" && f.salon_at >= end) return { where: "salon", text: "Забрать из салона нужно раньше конца окна" };
    if (direction === "end" && f.salon_at < end) return { where: "salon", text: "Вернуть в салон — только после окна забора" };
  }
  return { where: "", text: "" };
}

function AddressInput({ dealId, value, bad, onChange, onPick }: { dealId: number; value: string; bad: boolean; onChange: (v: string) => void; onPick: (s: Suggest) => void }) {
  const [list, setList] = useState<Suggest[]>([]);
  const [open, setOpen] = useState(false);
  const typed = useRef(false);
  useEffect(() => {
    if (!typed.current || value.trim().length < 3) return setList([]);
    const t = setTimeout(() => {
      suggestAddress(dealId, value)
        .then((r) => setList(r.results))
        .catch(() => setList([]));
    }, 300);
    return () => clearTimeout(t);
  }, [dealId, value]);
  return (
    <div className="relative">
      <textarea
        className={`${input} h-auto min-h-[52px] resize-y py-1.5 leading-snug ${bad ? "!border-err" : ""}`}
        rows={2}
        aria-invalid={bad}
        value={value}
        maxLength={180}
        placeholder="Город, улица и дом"
        aria-label="Адрес"
        onChange={(e) => {
          typed.current = true;
          setOpen(true);
          onChange(e.target.value);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && list.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-[var(--radius-ui)] border border-border bg-bg shadow-lg">
          {list.map((s) => (
            <li key={`${s.title}|${s.subtitle}`}>
              <button
                type="button"
                className="block w-full px-2 py-1.5 text-left text-[12.5px] hover:bg-bg-hover"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  typed.current = false;
                  setOpen(false);
                  onPick(s);
                }}
              >
                <div>{s.title}</div>
                {s.subtitle && <div className="text-[11.5px] text-fg-muted">{s.subtitle}</div>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Направления карточками: день и статус обоих видны всегда. */
function DirCards({ d, direction, onPick }: { d: DealDelivery; direction: Direction; onPick: (x: Direction) => void }) {
  return (
    <div className="grid grid-cols-2 gap-1.5" role="tablist" aria-label="Направление" data-annot="направления доставки">
      {DIRS.map((x) => {
        const s = d[x.id];
        const on = active(s);
        const chosen = x.id === direction;
        return (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={chosen}
            onClick={() => onPick(x.id)}
            className={`grid gap-1 rounded-[var(--radius-ui)] border p-2 text-left text-[12px] ${chosen ? "border-accent bg-accent/10" : "border-border bg-bg hover:bg-bg-hover"}`}
          >
            <span className="flex items-baseline justify-between gap-1 font-medium text-fg">
              <span className="truncate">{x.label}</span>
              <span className="flex-none text-[11px] font-normal text-fg-faint">{s.day ? dm(s.day) : ""}</span>
            </span>
            <span className={`flex items-center gap-1.5 ${on ? TONE[s.status_kind] : "text-fg-faint"}`}>
              <span className="inline-block h-1.5 w-1.5 flex-none rounded-full bg-current" />
              <span className="truncate">{on ? s.status_label || "нужна" : `${x.what} ${OFF[x.id]}`}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Steps({ stage, direction }: { stage: Stage; direction: Direction }) {
  const now = STEP[stage];
  return (
    <ol className="flex gap-1" aria-label="Этапы доставки" data-annot="шаги доставки">
      {STEPS[direction].map((t, i) => (
        <li
          key={t}
          aria-current={i === now ? "step" : undefined}
          className={`min-w-0 flex-1 truncate border-t-[3px] pt-1 text-[10.5px] ${i < now || (i === now && stage === "done") ? "border-ok text-fg-muted" : i === now ? "border-accent text-accent" : "border-border text-fg-faint"}`}
        >
          {t}
        </li>
      ))}
    </ol>
  );
}

/** Маршрут точками: откуда → куда со сроками; адрес целиком, части — строкой ниже. */
function RouteCard({ d, s, direction }: { d: DealDelivery; s: DealDeliverySide; direction: Direction }) {
  const day = s.time?.slice(0, 10) ?? s.day ?? "";
  const parts = [
    s.parts.apartment && `кв. ${s.parts.apartment}`,
    s.parts.entrance && `подъезд ${s.parts.entrance}`,
    s.parts.floor && `этаж ${s.parts.floor}`,
    s.parts.intercom && `домофон ${s.parts.intercom}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const when = s.window ? `${s.window.from}–${s.window.to}` : s.time ? `не позднее ${s.time.slice(11, 16)} · начало окна не задано` : "время не задано";
  const client = (
    <Stop key="client" title={direction === "start" ? "Доставить клиенту" : "Забрать у клиента"} last={direction === "start"}>
      <div className="whitespace-pre-line break-words">{s.parts.street || s.address || "адрес не задан"}</div>
      {parts && <div className="text-[11.5px] text-fg-muted">{parts}</div>}
      {s.note && <div className="text-[11.5px] text-fg-muted">{s.note}</div>}
      <div className="text-accent">
        {day ? `${dm(day)} · ` : ""}
        {when}
      </div>
    </Stop>
  );
  const salon = (
    <Stop key="salon" title={direction === "start" ? "Забрать из салона" : "Вернуть в салон"} last={direction === "end"}>
      <div className="break-words">{d.salon.address || d.salon.name || "салон не указан в сделке"}</div>
      <div className={s.time_salon ? "text-accent" : "text-fg-faint"}>
        {s.time_salon ? `${dm(s.time_salon)} · не позднее ${s.time_salon.slice(11, 16)}` : "срок салона не задан"}
      </div>
    </Stop>
  );
  return (
    <div className={`${card} grid gap-2`} data-annot="маршрут доставки">
      <BlockHead title="Маршрут" data={d} direction={direction} codes={[...BLOCK.address, ...BLOCK.when, ...BLOCK.contact]} />
      <div className="relative pl-5">{direction === "start" ? [salon, client] : [client, salon]}</div>
      {(s.phone || s.comment) && (
        <div className="grid gap-0.5 border-t border-border pt-2 text-[11.5px] text-fg-muted">
          {s.phone && <div>Телефон для курьера · {s.phone}</div>}
          {s.comment && <div className="break-words">Комментарий курьеру · {s.comment}</div>}
        </div>
      )}
    </div>
  );
}

function Stop({ title, last, children }: { title: string; last: boolean; children: React.ReactNode }) {
  return (
    <div className={`relative grid gap-0.5 ${last ? "" : "pb-3"}`}>
      {!last && <span className="absolute -left-[15px] top-3 bottom-0 w-px bg-border" />}
      <span className={`absolute -left-[19px] top-[5px] h-[9px] w-[9px] rounded-full border-2 bg-bg-subtle ${last ? "border-ok" : "border-accent"}`} />
      <div className="text-[11.5px] font-medium text-fg">{title}</div>
      {children}
    </div>
  );
}

/** Расхождение времени: окно без начала, другой интервал в тексте адреса — до сохранения. */
function TimeConflict({ s, f, onTake }: { s: DealDeliverySide; f: DeliveryForm; onTake: (w: [number, number]) => void }) {
  const inText = addressWindow(s.address);
  const saved = s.window ? [Number(s.window.from?.slice(0, 2)), Number(s.window.to?.slice(0, 2))] : null;
  const differs = inText && (!saved || saved[0] !== inText[0] || saved[1] !== inText[1]);
  if (!differs && (s.window || !s.time)) return null;
  const taken = inText && f.from === inText[0] && f.to === inText[1];
  return (
    <div className="rounded-[var(--radius-ui)] border border-border bg-warn-bg p-2.5 text-[12px] text-warn" data-annot="расхождение времени">
      <div className="font-medium">Время расходится — уточните у клиента</div>
      <div className="mt-0.5">
        {s.window ? `Окно ${s.window.from}–${s.window.to}` : s.time ? `В сделке только «не позднее ${s.time.slice(11, 16)}», начала окна нет` : ""}
        {inText ? `; в тексте адреса — ${hh(inText[0])}–${hh(inText[1])}` : ""}.
      </div>
      {inText && !taken && (
        <button type="button" className={`${link} mt-1`} onClick={() => onTake(inText)}>
          Взять {hh(inText[0])}–{hh(inText[1])} из адреса
        </button>
      )}
    </div>
  );
}

/** Поля журнала по блокам формы: у шапки блока — его история. */
const BLOCK = {
  address: ["address", "note"],
  when: ["window", "time", "time_salon"],
  contact: ["phone", "comment"],
  status: ["status", "courier", "courier_phone"],
} as const;

/** Шапка блока со значком истории: кто, когда и что менял именно здесь — раскрывается под шапкой. */
function BlockHead({ title, data, direction, codes }: { title: string; data: DealDelivery; direction: Direction; codes: readonly string[] }) {
  const [open, setOpen] = useState(false);
  const rows = data.changes.filter((c) => c.direction === direction && codes.includes(c.code));
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">{title}</span>
        {rows.length > 0 && (
          <button
            type="button"
            aria-expanded={open}
            title="Кто и что менял в этом блоке"
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex items-center gap-1 rounded px-1 text-[11px] ${open ? "text-accent" : "text-fg-faint hover:text-fg-muted"}`}
          >
            <History size={12} /> {rows.length}
          </button>
        )}
      </div>
      {open && (
        <ul className="mt-1.5 grid gap-1 rounded-[var(--radius-ui)] border border-border bg-bg p-2 text-[11.5px]" data-annot={`история: ${title}`}>
          {rows.map((c, i) => (
            <li key={i} className="grid">
              <span className="text-fg-faint">
                {dm(c.at)} {c.at.slice(11, 16)} · {c.who || c.source_label}
                {c.who ? ` · ${c.source_label}` : ""}
              </span>
              <span className="break-words">
                {c.field}: <span className="text-fg-muted line-through decoration-fg-faint">{c.old || "пусто"}</span> → {c.new || "пусто"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Подпись ошибки под полем — красным. */
const Err = ({ text }: { text: string }) => (text ? <div className="text-[11.5px] text-err">{text}</div> : null);

/** Одна форма направления: адрес, когда, срок салона, контакт, общий комментарий. */
function Editor({
  dealId,
  direction,
  data,
  f,
  change,
  bad,
}: {
  dealId: number;
  direction: Direction;
  data: DealDelivery;
  f: DeliveryForm;
  change: (patch: Partial<DeliveryForm>) => void;
  bad: Problem;
}) {
  const s = data[direction];
  const end = direction === "end";
  return (
    <div className="grid gap-2" data-annot={`форма доставки: ${direction}`}>
      <TimeConflict s={s} f={f} onTake={([from, to]) => change({ from, to })} />
      <div className={card}>
        <BlockHead title={end ? "Откуда забрать" : "Куда доставить"} data={data} direction={direction} codes={BLOCK.address} />
        <div className="grid gap-2">
          <AddressInput
            dealId={dealId}
            value={f.address}
            bad={bad.where === "address"}
            onChange={(v) => change({ address: v, uri: null, lat: null, lon: null, geo: null })}
            onPick={(p) => change({ address: p.title, uri: p.uri || null, lat: null, lon: null, geo: null })}
          />
          {bad.where === "address" && <Err text={bad.text} />}
          <div className="grid grid-cols-4 gap-1.5">
            <label className={field}>
              Кв.
              <input className={input} value={f.apartment} maxLength={8} onChange={(e) => change({ apartment: e.target.value })} />
            </label>
            <label className={field}>
              Подъезд
              <input className={input} value={f.entrance} maxLength={4} onChange={(e) => change({ entrance: e.target.value })} />
            </label>
            <label className={field}>
              Этаж
              <input className={input} value={f.floor} maxLength={4} onChange={(e) => change({ floor: e.target.value })} />
            </label>
            <label className={field}>
              Домофон
              <input className={input} value={f.intercom} maxLength={12} onChange={(e) => change({ intercom: e.target.value })} />
            </label>
          </div>
          <label className={field}>
            Ориентир и пожелание курьеру
            <input className={input} value={f.note} maxLength={200} placeholder="вход со двора, позвонить за час…" onChange={(e) => change({ note: e.target.value })} />
          </label>
        </div>
      </div>
      <div className={card}>
        <BlockHead title={end ? "Когда забрать у клиента" : "Когда доставить клиенту"} data={data} direction={direction} codes={BLOCK.when} />
        <div className="grid gap-1.5">
          <WindowPick
            label={end ? "Забрать" : "Доставить"}
            day={f.day}
            from={f.from}
            to={f.to}
            anchor={s.day ?? f.day}
            bad={bad.where === "window"}
            onChange={(day, from, to) => change({ day, from, to })}
          />
          {bad.where === "window" && <Err text={bad.text} />}
          <HourPick
            label={end ? "В салон до" : "Из салона до"}
            value={f.salon_at}
            anchor={f.day}
            bad={bad.where === "salon"}
            suggest={salonSuggest(f, direction)}
            onChange={(v) => change({ salon_at: v })}
          />
          {bad.where === "salon" && <Err text={bad.text} />}
        </div>
      </div>
      <div className={card}>
        <BlockHead title="Контакт для курьера" data={data} direction={direction} codes={BLOCK.contact} />
        <input className={input} value={f.phone} maxLength={20} placeholder="Телефон клиента" aria-label="Телефон для курьера" onChange={(e) => change({ phone: e.target.value })} />
        <details className="mt-2">
          <summary className="cursor-pointer text-[11.5px] text-fg-muted">Общий комментарий курьеру · оба направления{f.comment ? " · есть" : ""}</summary>
          <textarea
            className={`${input} mt-1.5 h-auto min-h-[52px] resize-y py-1.5`}
            rows={2}
            maxLength={512}
            value={f.comment ?? ""}
            aria-label="Комментарий курьеру"
            onChange={(e) => change({ comment: e.target.value })}
          />
        </details>
      </div>
    </div>
  );
}

function OfferRow({ title, hint, price, chosen, disabled, onPick }: { title: string; hint: string; price: number; chosen: boolean; disabled?: string | undefined; onPick?: (() => void) | undefined }) {
  return (
    <button
      type="button"
      aria-pressed={chosen}
      disabled={!onPick || !!disabled}
      title={disabled}
      onClick={onPick}
      className={`flex w-full items-center gap-2 rounded-[var(--radius-ui)] border p-2 text-left text-[12.5px] disabled:cursor-default ${chosen ? "border-accent bg-accent/10" : "border-border bg-bg hover:bg-bg-hover"} ${disabled ? "opacity-60" : ""}`}
    >
      <span className={`h-3.5 w-3.5 flex-none rounded-full border ${chosen ? "border-[4px] border-accent" : "border-fg-muted"}`} />
      <span className="min-w-0">
        <span className="block">{title}</span>
        <span className="block text-[11px] text-fg-muted">{disabled ?? hint}</span>
      </span>
      <span className="ml-auto flex-none font-medium tabular-nums">{rub(price)}</span>
    </button>
  );
}

/** Тарифы списком с выбором: Яндекс «к началу окна», Яндекс «сейчас», Dostavista. */
function Prices({ t, day, pick, onPick, dostavista }: { t: Tariff; day: string; pick: Pick | null; onPick?: ((p: Pick) => void) | undefined; dostavista: boolean }) {
  const y = t.yandex;
  const d = t.dostavista;
  const chosen = (provider: string, variant: string) => pick?.provider === provider && pick.variant === variant;
  const yandexPick = (variant: string, o: Offer, label: string) =>
    onPick ? () => onPick({ provider: "yandex", variant, label, price: o.price }) : undefined;
  const atLocked = y.at_open ? `заказать можно с ${dm(y.at_open)} ${y.at_open.slice(11, 16)}: Яндекс берёт заказ не раньше чем за 7 ч` : undefined;
  return (
    <div className="grid gap-1.5" data-annot="стоимость доставки">
      <div className="flex items-baseline justify-between">
        <span className="font-medium">Тариф</span>
        {d.stand === "test" && <span className="text-[11px] text-warn">Dostavista: тестовый стенд</span>}
      </div>
      {d.fee != null && (
        <OfferRow
          title="Dostavista"
          hint={`курьер к окну клиента${d.total != null ? ` · к оплате ${rub(d.total)}` : ""}`}
          price={d.total ?? d.fee}
          chosen={chosen("dostavista", "")}
          disabled={dostavista ? undefined : "заказ — когда prokat получит ключ Dostavista"}
          onPick={onPick && (() => onPick({ provider: "dostavista", variant: "", label: "Dostavista", price: d.total ?? d.fee! }))}
        />
      )}
      {d.skip && <div className="text-[11.5px] text-fg-muted">Dostavista: {d.skip}</div>}
      {d.error && <div className="text-[11.5px] text-err">Dostavista: {d.error}</div>}
      {y.error && <div className="text-[11.5px] text-err">Яндекс: {y.error}</div>}
      {(y.at ?? [])
        .filter((o) => o.code === "express")
        .map((o) => (
          <OfferRow key="at" title="Яндекс · к началу окна" hint="экспресс заранее, по полной цене" price={o.price} chosen={chosen("yandex", "at")} disabled={atLocked} onPick={yandexPick("at", o, "Яндекс «к началу окна»")} />
        ))}
      {y.now.map((o) => (
        <OfferRow
          key={o.code}
          title={`Яндекс · ${o.label}`}
          hint={`если заказать сейчас — довезут до ${at(o.delivery_to, day)}${day === today() ? "" : ", не к окну"}`}
          price={o.price}
          chosen={chosen("yandex", o.code)}
          onPick={yandexPick(o.code, o, `Яндекс «${o.label}»`)}
        />
      ))}
      {!y.error && y.now.length > 0 && <div className="text-[11px] text-fg-faint">Окна 2 и 4 часа Яндекс даёт только в день доставки.</div>}
    </div>
  );
}

/** Карта виджетом; «Развернуть» — окно почти на весь экран, «Яндекс Карты» — сайт в новой вкладке. */
function RouteMap({ src, href }: { src: string; href: string | null }) {
  const [big, setBig] = useState(false);
  useEffect(() => {
    if (!big) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setBig(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [big]);
  const links = (
    <div className="flex items-center gap-3 text-[11.5px]">
      <button type="button" className="inline-flex items-center gap-1 text-accent hover:underline" onClick={() => setBig((b) => !b)}>
        {big ? <X size={12} /> : <Maximize2 size={12} />} {big ? "Закрыть" : "Развернуть"}
      </button>
      {href && (
        <a className="inline-flex items-center gap-1 text-accent hover:underline" href={href} target="_blank" rel="noreferrer">
          <ExternalLink size={12} /> Открыть в Яндекс Картах
        </a>
      )}
    </div>
  );
  return (
    <div className="grid gap-1" data-annot="карта маршрута">
      {links}
      <iframe title="Маршрут доставки" src={src} className="h-[220px] w-full rounded-[var(--radius-ui)] border border-border" loading="lazy" />
      {big && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setBig(false)} role="dialog" aria-label="Карта маршрута">
          <div className="flex h-[90vh] w-[92vw] flex-col gap-2 rounded-[var(--radius-card)] bg-bg p-3" onClick={(e) => e.stopPropagation()}>
            {links}
            <iframe title="Маршрут доставки крупно" src={src} className="min-h-0 w-full flex-1 rounded-[var(--radius-ui)] border border-border" />
          </div>
        </div>
      )}
    </div>
  );
}

/** Выбранный тариф → расход компании, цена клиенту и «Заказать курьера за …» (нижняя полоса). */
function OrderBox({ dealId, direction, data, f, pick }: { dealId: number; direction: Direction; data: DealDelivery; f: DeliveryForm; pick: Pick }) {
  const qc = useQueryClient();
  const [price, setPrice] = useState(() => clientPrice(pick.price, data.orders.markup));
  const [name, setName] = useState("");
  useEffect(() => setPrice(clientPrice(pick.price, data.orders.markup)), [pick, data.orders.markup]);
  const order = useMutation({
    mutationFn: () => orderCourier(dealId, direction, { ...f, provider: pick.provider, variant: pick.variant, client_price: price, name }),
    onSuccess: (r) => {
      if (r.card) qc.setQueryData(dealDeliveryKey(dealId), { ...r.card, orders: { ...data.orders, [direction]: r.order }, can_order: data.can_order });
      else qc.invalidateQueries({ queryKey: dealDeliveryKey(dealId) });
    },
  });
  const off = !data.orders.enabled;
  const where = direction === "start" ? `${data.salon.address} → ${f.address}` : `${f.address} → ${data.salon.address}`;
  const go = () => {
    if (window.confirm(`Заказать ${pick.label}?\n${where}\n${f.day} ${f.from >= 0 ? hh(f.from) : ""}–${hh(f.to)}\n\nКомпания оплатит ${rub(pick.price)}, клиенту в договор — ${rub(price)}.`)) order.mutate();
  };
  return (
    <div className="grid gap-1.5" data-annot="заказ курьера">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[11px] text-fg-muted">Расход компании</div>
          <div className="text-[15px] font-semibold tabular-nums">{rub(pick.price)}</div>
        </div>
        <label className={field}>
          Клиенту в договор, ₽
          <input type="number" min={0} max={100000} step={100} className={input} value={price} onChange={(e) => setPrice(Number(e.target.value))} />
        </label>
      </div>
      <input className={input} value={name} maxLength={64} placeholder="Имя получателя для курьера" aria-label="Имя для курьера" onChange={(e) => setName(e.target.value)} />
      <button type="button" className={btnMain} disabled={!data.can_order || off || order.isPending || price < 0} onClick={go}>
        <Truck size={14} /> {order.isPending ? "Заказываю…" : `Заказать курьера за ${rub(pick.price)}`}
      </button>
      {!data.can_order && <span className="text-[11.5px] text-fg-muted">нужно право «Заказывать курьера»</span>}
      {data.can_order && off && <span className="text-[11.5px] text-warn">заказ из prokat пока выключен</span>}
      {order.error && <div className="text-[12px] text-err">Не заказано: {order.error.message}</div>}
    </div>
  );
}

const CONTRACT_ORDER: Record<string, string> = {
  ok: "строка «Доставка» в договоре",
  off: "строка в договор не добавлена — запись договора из prokat выключена, поставьте руками",
  no_contract: "договора нет — строку не добавили",
  failed: "строку в договор добавить не удалось",
};

/** Карточка заявки: служба, шаг, курьер, где он, договор, сообщения клиенту, отмена. */
function CourierCard({ dealId, o, data, direction }: { dealId: number; o: CourierOrder; data: DealDelivery; direction: Direction }) {
  const qc = useQueryClient();
  const put = (x: CourierOrder) => qc.setQueryData(dealDeliveryKey(dealId), { ...data, orders: { ...data.orders, [direction]: x } });
  const refresh = useMutation({ mutationFn: () => refreshCourier(dealId, o.id), onSuccess: (r) => put(r.order) });
  const cancel = useMutation({
    mutationFn: (paid: boolean) => cancelCourier(dealId, o.id, paid),
    onSuccess: () => qc.invalidateQueries({ queryKey: dealDeliveryKey(dealId) }),
    onError: (e) => {
      if (e instanceof ApiFailure && e.code === "CANCEL_PAID" && window.confirm(e.message)) cancel.mutate(true);
    },
  });
  const row = (k: string, v: React.ReactNode) =>
    v ? (
      <div className="flex gap-2">
        <span className="w-[86px] flex-none text-fg-muted">{k}</span>
        <span className="min-w-0 break-words">{v}</span>
      </div>
    ) : null;
  const sent = Object.entries(o.notified)
    .map(([k, v]) => `${k === "found" ? "назначен" : "выехал"}: ${v.preview ? "предпросмотр" : (v.to ?? []).join(", ") || "некуда"}`)
    .join(" · ");
  return (
    <div className={`${card} grid gap-1`} data-annot="заявка на курьера">
      <div className="flex items-center gap-2">
        <Truck size={14} className={o.live ? "text-accent" : "text-fg-muted"} />
        <span className="font-medium">
          {o.provider_label} №{o.external_id || "—"}
        </span>
        <span className={`rounded px-1.5 py-0.5 text-[11px] ${o.live ? "bg-ok-bg text-ok" : "bg-neutral-bg text-fg-muted"}`}>{o.state_label}</span>
        {o.stand === "test" && <span className="text-[11.5px] text-warn">тест</span>}
        <button type="button" className="ml-auto text-fg-muted hover:text-fg" title="Спросить службу сейчас" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          <RefreshCw size={13} className={refresh.isPending ? "animate-spin" : ""} />
        </button>
      </div>
      {row("Вариант", o.variant_label)}
      {row("Цена", o.price != null ? `${rub(o.price)} служба · ${o.client_price != null ? rub(o.client_price) : "—"} клиенту` : "")}
      {row("Курьер", [o.courier_name, o.car].filter(Boolean).join(", "))}
      {row("Телефон", o.courier_phone && <a className="text-accent" href={`tel:${o.courier_phone.replace(/\s/g, "")}`}>{o.courier_phone}</a>)}
      {row("Довезут к", o.eta ? at(o.eta, o.eta.slice(0, 10)) : "")}
      {row("Слежение", o.tracking_url && <a className="text-accent" href={o.tracking_url} target="_blank" rel="noreferrer">открыть</a>)}
      {row("Договор", o.contract_state ? `${CONTRACT_ORDER[o.contract_state] ?? o.contract_state}${o.contract_detail ? ` · ${o.contract_detail}` : ""}` : "")}
      {row("Клиенту", sent)}
      {row("Заказал", `${o.user_name}${o.date ? `, ${dm(o.date)} ${o.date.slice(11, 16)}` : ""}`)}
      {o.error && <div className="text-[12px] text-err">{o.error}</div>}
      {o.courier && <RouteMap src={mapUrl(o.courier, null)!} href={mapsUrl(o.courier, null)} />}
      {o.live && data.can_order && (
        <div>
          <button type="button" className={`${btn} text-err`} disabled={cancel.isPending} onClick={() => window.confirm("Отменить заказ курьера? Доставка останется нужной.") && cancel.mutate(false)}>
            {cancel.isPending ? "Отменяю…" : "Отменить заказ курьера"}
          </button>
        </div>
      )}
      {cancel.error && !(cancel.error instanceof ApiFailure && cancel.error.code === "CANCEL_PAID") && <div className="text-[12px] text-err">Не отменено: {cancel.error.message}</div>}
    </div>
  );
}

/** Статус по шагам службы + «курьер заказан вне prokat / статус вручную» — поля Bitrix как есть. */
function StatusCard({ dealId, direction, data, open }: { dealId: number; direction: Direction; data: DealDelivery; open: boolean }) {
  const qc = useQueryClient();
  const s = data[direction];
  const steps = data.statuses[direction].filter((x) => x.kind === "go" || x.kind === "done");
  const now = steps.findIndex((x) => x.code === s.status);
  const [status, setStatus] = useState(s.status);
  const [courier, setCourier] = useState(s.courier);
  const [phone, setPhone] = useState(s.courier_phone);
  const body: DeliveryEdit = {};
  if (status && status !== s.status) body.status = status;
  if (courier.trim() !== s.courier) body.courier = courier.trim();
  if (phone.trim() !== s.courier_phone) body.courier_phone = phone.trim();
  const save = useMutation({
    mutationFn: () => saveDelivery(dealId, direction, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: dealDeliveryKey(dealId) }),
  });
  return (
    <div className={`${card} grid gap-2`} data-annot="статус доставки">
      <BlockHead title="Статус и курьер" data={data} direction={direction} codes={BLOCK.status} />
      {now >= 0 && (
        <ul className="grid gap-0.5">
          {steps.map((x, i) => (
            <li key={x.code} className={`flex items-center gap-2 ${i <= now ? "text-fg" : "text-fg-faint"}`}>
              {i <= now ? <Check size={13} className="text-ok" /> : <span className="inline-block h-[13px] w-[13px] rounded-full border border-border" />}
              {x.label}
              {i === now && <span className="ml-auto text-[11px] text-fg-muted">сейчас</span>}
            </li>
          ))}
        </ul>
      )}
      {(s.courier || s.courier_phone) && (
        <div className="text-[12px]">
          <span className="text-fg-muted">Курьер в сделке · </span>
          {[s.courier, s.courier_phone].filter(Boolean).join(" · ")}
        </div>
      )}
      {data.can_edit && (
        <details open={open}>
          <summary className="cursor-pointer text-[11.5px] text-fg-muted">Курьер заказан вне prokat · статус вручную</summary>
          <div className="mt-2 grid gap-1.5">
            <label className={field}>
              Статус в сделке
              <select className={input} value={status} onChange={(e) => setStatus(e.target.value)}>
                {!s.status && <option value="">не задан</option>}
                {data.statuses[direction]
                  .filter((x) => x.kind !== "cancel")
                  .map((x) => (
                    <option key={x.code} value={x.code}>
                      {x.label}
                    </option>
                  ))}
              </select>
            </label>
            <label className={field}>
              Служба или ссылка на заказ
              <input className={input} value={courier} maxLength={255} placeholder="Dostavista №…" onChange={(e) => setCourier(e.target.value)} />
            </label>
            <label className={field}>
              Телефон курьера
              <input className={input} value={phone} maxLength={128} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <button type="button" className={btn} disabled={!Object.keys(body).length || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Записываю…" : "Записать в сделку"}
            </button>
            {save.error && <div className="text-[12px] text-err">Не записано: {save.error.message}</div>}
          </div>
        </details>
      )}
    </div>
  );
}

function Log({ data, direction }: { data: DealDelivery; direction: Direction }) {
  const rows = data.changes.filter((c) => c.direction === direction);
  if (rows.length === 0) return null;
  return (
    <details className={card} data-annot="журнал доставки">
      <summary className="cursor-pointer text-fg-muted">Журнал правок · {rows.length}</summary>
      <ul className="mt-2 grid gap-1">
        {rows.map((c, i) => (
          <li key={i} className="text-[12px]">
            <span className="text-fg-faint">
              {dm(c.at)} {c.at.slice(11, 16)}
            </span>{" "}
            {c.field}: {c.old || "—"} → {c.new || "—"}{" "}
            <span className="text-fg-faint">
              · {c.source_label}
              {c.who ? `, ${c.who}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

type Sub = "courier" | "track";

/** Переключатель подвкладок: крупнее переключателя направлений под ним. */
function SubTabs({ sub, onSub, tracks, active: going }: { sub: Sub; onSub: (s: Sub) => void; tracks: number | null; active: number }) {
  const items: { id: Sub; label: string; hint: string }[] = [
    { id: "courier", label: "Курьер", hint: "по городу" },
    { id: "track", label: "Трекинг", hint: tracks === null ? "" : tracks ? `${tracks}${going ? ` · в пути ${going}` : ""}` : "нет" },
  ];
  return (
    <div className="grid grid-cols-2 gap-1 rounded-[var(--radius-ui)] bg-bg-hover p-1" role="tablist" aria-label="Доставка" data-annot="подвкладки доставки">
      {items.map((x) => (
        <button
          key={x.id}
          type="button"
          role="tab"
          aria-selected={x.id === sub}
          onClick={() => onSub(x.id)}
          className={`rounded-[var(--radius-ui)] px-2 py-1.5 text-[13px] ${x.id === sub ? "bg-bg font-semibold text-fg shadow-sm" : "text-fg-muted hover:text-fg"}`}
        >
          {x.label}
          {x.hint && <span className="ml-1.5 text-[11.5px] font-normal text-fg-faint">{x.hint}</span>}
        </button>
      ))}
    </div>
  );
}

export function DeliveryTab({ dealId, onOffer }: { dealId: number; onOffer?: OnOffer | undefined }) {
  const [params, setParams] = useSearchParams();
  const tq = useQuery({ queryKey: dealTracksKey(dealId), queryFn: () => loadDealTracks(dealId) });
  const q = useQuery({ queryKey: dealDeliveryKey(dealId), queryFn: () => loadDealDelivery(dealId) });
  const tracks = tq.data?.tracks ?? null;
  const raw = params.get("dsub");
  const ordered = q.data ? !!(q.data.start.status_label || q.data.end.status_label || q.data.orders?.start || q.data.orders?.end) : true;
  const sub: Sub = raw === "track" || raw === "courier" ? raw : tracks?.length && !ordered ? "track" : "courier";
  const setSub = (s: Sub) =>
    setParams(
      (p) => {
        p.set("dsub", s);
        return p;
      },
      { replace: true },
    );
  return (
    <div className="flex flex-col gap-2 p-3">
      <SubTabs sub={sub} onSub={setSub} tracks={tracks ? tracks.length : null} active={tracks?.filter((t) => t.active && !t.cancelled).length ?? 0} />
      {sub === "track" ? <DealTracks dealId={dealId} onOffer={onOffer} /> : <Courier dealId={dealId} q={q} />}
    </div>
  );
}

/** «Курьер»: направления карточками, у выбранного — шаги и главное действие внизу. */
function Courier({ dealId, q }: { dealId: number; q: ReturnType<typeof useQuery<DealDelivery>> }) {
  const [direction, setDirection] = useState<Direction>(() => {
    const d = q.data;
    return d && !active(d.start) && active(d.end) ? "end" : "start";
  });
  if (q.isPending) return <div className={note}>Загружаю доставку…</div>;
  if (q.isError) return <div className={note}>Доставка не загрузилась: {q.error.message}</div>;
  const d = q.data;
  const tz = d.tz ? `МСК${d.tz > 0 ? "+" : ""}${d.tz}` : "= МСК";
  return (
    <>
      <DirCards d={d} direction={direction} onPick={setDirection} />
      <div className="text-[11px] text-fg-faint">
        Время салона · {d.salon.name || d.salon.short || "салон не указан"} {tz}
      </div>
      <DirectionView key={`${dealId}-${direction}`} dealId={dealId} direction={direction} data={d} onDirection={setDirection} />
    </>
  );
}

function DirectionView({ dealId, direction, data, onDirection }: { dealId: number; direction: Direction; data: DealDelivery; onDirection: (x: Direction) => void }) {
  const qc = useQueryClient();
  const s = data[direction];
  const dir = DIRS.find((x) => x.id === direction)!;
  const order = data.orders?.[direction] ?? null;
  const stage = stageOf(s, !!order?.live);
  const [editing, setEditing] = useState(false);
  const [manual, setManual] = useState(false);
  const [f, setF] = useState<DeliveryForm>(() => initial(s, data.phones));
  const [pick, setPick] = useState<Pick | null>(null);
  const tariff = useMutation({
    mutationFn: (form: DeliveryForm) => deliveryTariff(dealId, direction, form),
    onSuccess: (t) => {
      setPick(null);
      if (t.point) setF((x) => ({ ...x, lat: t.point!.lat, lon: t.point!.lon, geo: t.point!.geo }));
    },
  });
  const save = useMutation({
    mutationFn: ({ calc }: { calc: boolean }) => saveDealDelivery(dealId, direction, f).then((r) => ({ r, calc })),
    onSuccess: ({ r, calc }) => {
      qc.setQueryData(dealDeliveryKey(dealId), { ...r.card, orders: data.orders, can_order: data.can_order });
      setEditing(false);
      if (calc) tariff.mutate(f);
    },
  });
  // «Оформить» = тот же переключатель «нужна»: статус «Нужна доставка» и галка в договоре, дальше — форма.
  const enable = useMutation({
    mutationFn: () => saveDelivery(dealId, direction, { need: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: dealDeliveryKey(dealId) }),
  });
  // Смена адреса, окна или срока — прежний расчёт и выбранный тариф больше не про этот маршрут.
  const change = (patch: Partial<DeliveryForm>) => {
    setF((x) => ({ ...x, ...patch }));
    setPick(null);
    tariff.reset();
    save.reset();
  };
  const form = data.can_edit && (stage === "edit" || editing);
  const bad = problem(f, direction);
  const why = bad.text;
  const saved = save.data?.r;
  const other: Direction = direction === "start" ? "end" : "start";
  const live = order?.live ?? false;
  const client: Geo | null = s.geo ?? (f.lat != null && f.lon != null ? { lat: f.lat, lon: f.lon } : null);
  const salon: Geo | null = data.salon.lat != null && data.salon.lon != null ? { lat: data.salon.lat, lon: data.salon.lon } : null;
  const map = client ? (direction === "start" ? mapUrl(salon, client) : mapUrl(client, salon)) : null;
  const maps = client ? (direction === "start" ? mapsUrl(salon, client) : mapsUrl(client, salon)) : null;
  const heading =
    stage === "none" ? dir.what : form ? (direction === "start" ? "Адрес и время доставки" : "Адрес и время забора") : s.status_label || dir.what;
  return (
    <div className="flex flex-col gap-2" data-annot={`направление: ${dir.label}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[15px] font-semibold leading-tight">{heading}</div>
          <div className="text-[11.5px] text-fg-muted">
            {s.day ? `${dm(s.day)} · ` : ""}
            {dir.route}
          </div>
        </div>
        {data.can_edit && (
          <DeliveryNeed deal={dealId} direction={direction} on={active(s)} onSaved={() => qc.invalidateQueries({ queryKey: dealDeliveryKey(dealId) })} />
        )}
      </div>
      {stage !== "none" && <Steps stage={stage} direction={direction} />}
      {direction === "end" && data.start.status_kind === "done" && (
        <div className="rounded-[var(--radius-ui)] bg-ok-bg px-2.5 py-1.5 text-[12px] text-ok">✓ Доставка клиенту завершена{data.start.day ? ` · ${dm(data.start.day)}` : ""}</div>
      )}

      {stage === "none" && (
        <div className="grid justify-items-center gap-1 px-4 py-6 text-center" data-annot="доставка не нужна">
          <Package size={40} strokeWidth={1.3} className="text-fg-faint" />
          <div className="text-[14px] font-medium">
            {dir.what} {OFF[direction]}
          </div>
          <div className="text-[12px] text-fg-muted">{direction === "start" ? "Клиент сам заберёт платье в салоне." : "Клиент сам вернёт платье в салон."}</div>
          <div className="mt-2 text-[11.5px] text-fg-faint">{data.salon.address}</div>
        </div>
      )}

      {stage !== "none" && (form ? <Editor dealId={dealId} direction={direction} data={data} f={f} change={change} bad={bad} /> : <RouteCard d={data} s={s} direction={direction} />)}
      {stage === "edit" && !data.can_edit && (
        <div className="rounded-[var(--radius-ui)] bg-warn-bg px-2.5 py-1.5 text-[12px] text-warn">Данных для курьера не хватает: адрес, окно или время не заданы.</div>
      )}
      {!form && map && stage !== "none" && (
        <details className={card}>
          <summary className="flex cursor-pointer items-center gap-1 text-fg-muted">
            <MapPin size={12} /> Карта маршрута
          </summary>
          <div className="mt-2">
            <RouteMap src={map} href={maps} />
          </div>
        </details>
      )}
      {stage === "ready" && !form && tariff.data && (
        <Prices t={tariff.data} day={f.day} pick={pick} onPick={live || !data.orders ? undefined : setPick} dostavista={data.orders?.dostavista ?? false} />
      )}
      {tariff.error && <div className="text-[12px] text-err">Не посчитано: {tariff.error.message}</div>}
      {order && <CourierCard dealId={dealId} o={order} data={data} direction={direction} />}
      {(stage === "courier" || stage === "done" || (stage === "ready" && !form)) && <StatusCard key={`${s.status}|${s.courier}|${s.courier_phone}`} dealId={dealId} direction={direction} data={data} open={manual} />}
      {saved && (
        <div className={`text-[12px] ${contractFailed(saved.contract) ? "text-err" : "text-ok"}`}>
          Bitrix: {saved.bitrix === "pending" ? "не ответил, дошлём сами" : saved.bitrix === "same" ? "без изменений" : "записан"} · {CONTRACT_TEXT[saved.contract]}
        </div>
      )}
      <Log data={data} direction={direction} />

      <div className="sticky bottom-0 -mx-3 mt-1 grid gap-1.5 border-t border-border bg-bg-subtle px-3 py-2.5" data-annot="главное действие">
        {stage === "none" &&
          (data.can_edit ? (
            <>
              <button type="button" className={btnMain} disabled={enable.isPending} onClick={() => enable.mutate()}>
                {enable.isPending ? "Записываю…" : direction === "start" ? "Оформить доставку" : "Оформить забор"}
              </button>
              {enable.error && <span className="text-[12px] text-err">Не записано: {enable.error.message}</span>}
            </>
          ) : (
            <span className="text-[11.5px] text-fg-muted">Только просмотр</span>
          ))}
        {stage !== "none" && form && (
          <>
            <span className={`text-[11.5px] ${why || save.error ? "text-err" : "text-fg-muted"}`}>{save.error ? `Не записано: ${save.error.message}` : why || "Сохраним адрес, время и контакты в сделку и договор"}</span>
            <button type="button" className={btnMain} disabled={!!why || save.isPending} onClick={() => save.mutate({ calc: true })}>
              {save.isPending ? "Записываю…" : "Сохранить и рассчитать"}
            </button>
            <div className="grid grid-cols-2 gap-1.5">
              <button type="button" className={btn} disabled={!!why || save.isPending} onClick={() => save.mutate({ calc: false })}>
                Сохранить без расчёта
              </button>
              {stage !== "edit" ? (
                <button
                  type="button"
                  className={btn}
                  onClick={() => {
                    setEditing(false);
                    setF(initial(s, data.phones));
                  }}
                >
                  Отмена
                </button>
              ) : (
                <span />
              )}
            </div>
          </>
        )}
        {stage === "ready" && !form && (
          <>
            {pick && !live && data.orders ? (
              <OrderBox dealId={dealId} direction={direction} data={data} f={f} pick={pick} />
            ) : (
              <>
                {tariff.data && <span className="text-[11.5px] text-fg-muted">{live ? "Курьер уже заказан — новый можно после отмены" : "Выберите тариф выше"}</span>}
                {!tariff.data && (
                  <button type="button" className={btnMain} disabled={tariff.isPending} onClick={() => tariff.mutate(f)}>
                    {tariff.isPending ? "Считаю…" : "Рассчитать стоимость"}
                  </button>
                )}
              </>
            )}
            {data.can_edit && (
              <div className="flex justify-between gap-2">
                <button type="button" className={link} onClick={() => setEditing(true)}>
                  Изменить данные
                </button>
                <button type="button" className={link} onClick={() => setManual(true)}>
                  Курьер уже заказан вне prokat
                </button>
              </div>
            )}
          </>
        )}
        {stage === "courier" && <span className="text-[11.5px] text-fg-muted">Следующий шаг: {next(s, direction, data.statuses[direction])}</span>}
        {stage === "done" &&
          (direction === "start" ? (
            <button type="button" className={btnMain} onClick={() => onDirection(other)}>
              Перейти к забору
            </button>
          ) : (
            <span className="text-center text-[12px] text-ok">✓ Платье вернулось в салон</span>
          ))}
      </div>
    </div>
  );
}
